import { Checkbox, type CheckboxProps } from '@progress/kendo-react-inputs'
import { useAtom } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { VStack } from '~/components/elements/VStack'
import { useLogEvent } from '~/hooks/useLogEvent'
import { toggleItem } from '~/utils'

import { hiddenBrandIdsAtom, hiddenLabelBrandIdsAtom } from '../../atoms'
import { type SurveyGroupBrand } from '../../hooks/useMainQuery'

import styles from './BrandCheckboxes.css'

export type BrandCheckboxesProps = {
  brand: SurveyGroupBrand
}

export const BrandCheckboxes = ({ brand }: BrandCheckboxesProps) => {
  const { t } = useTranslation()
  const [hiddenBrandIds, setHiddenBrandIds] = useAtom(hiddenBrandIdsAtom)
  const [hiddenLabelBrandIds, setHiddenLabelBrandIds] = useAtom(
    hiddenLabelBrandIdsAtom,
  )

  const isBrandChecked = !hiddenBrandIds.includes(brand.id)
  const isLabelChecked = !hiddenLabelBrandIds.includes(brand.id)

  const logEvent = useLogEvent()
  const handleBrandChange = React.useCallback<
    EH<CheckboxProps, 'onChange'>
  >(() => {
    setHiddenBrandIds(toggleItem(brand.id))
    logEvent('Change brands to be displayed', {
      eventProperties: [{ key: 'brand', value: brand.name }],
    })
  }, [brand.id, brand.name, logEvent, setHiddenBrandIds])

  const handleLabelChange = React.useCallback<
    EH<CheckboxProps, 'onChange'>
  >(() => {
    setHiddenLabelBrandIds(toggleItem(brand.id))
    logEvent(`Turn ${isLabelChecked ? 'off' : 'on'} numbers`, {
      eventProperties: [{ key: 'brand', value: brand.name }],
    })
  }, [brand.id, brand.name, isLabelChecked, logEvent, setHiddenLabelBrandIds])

  return (
    <VStack spacing={1}>
      <Checkbox
        checked={isBrandChecked}
        label={brand.name}
        onChange={handleBrandChange}
      />

      <Checkbox
        checked={isLabelChecked}
        className={styles.labelCheckbox}
        disabled={!isBrandChecked}
        label={t('data_labels')}
        onChange={handleLabelChange}
      />
    </VStack>
  )
}
